import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  REPORT_MAX_DAYS,
  type ReportRangeAdjustment,
  type TimeRange,
  formatDayMonthYear,
} from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import { openDateTimePicker } from "../Logger/dateTimePicker";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { webTapFloor, type UiTheme } from "../theme";
import { useDateLocale } from "../primitives/DateFormatContext";

// PG-64: the report's period, two dates. Each opens the platform's own date
// picker through the Logger's seam (Android dialog, a browser date input on
// web). The owner clamps the pick (reportRangeAfterPick) and passes back
// what it changed, which is said under the dates.
export function ReportPeriodPicker({
  theme,
  range,
  onPick,
  adjusted,
}: {
  theme: UiTheme;
  range: TimeRange;
  onPick: (end: "from" | "to", pickedMs: number) => void;
  /** How the last pick was corrected; null when it landed as picked. */
  adjusted: ReportRangeAdjustment | null;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const styles = makeStyles(theme);
  const field = (end: "from" | "to") => {
    const ms = end === "from" ? range.fromMs : range.toMs;
    const label =
      end === "from"
        ? t("mobile.progress.report.from", { defaultValue: "From" })
        : t("mobile.progress.report.to", { defaultValue: "To" });
    const value = formatDayMonthYear(new Date(ms), dateLocale);
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("mobile.progress.report.pickA11y", {
          label,
          value,
          defaultValue: "{{label}}: {{value}}. Change the date",
        })}
        onPress={() =>
          openDateTimePicker({
            value: new Date(ms),
            mode: "date",
            onChange: (event, picked) => {
              if (event.type === "set" && picked) onPick(end, picked.getTime());
            },
          })
        }
        style={({ pressed }) => [styles.field, pressed && styles.pressed]}
      >
        <Text style={styles.label}>{label}</Text>
        <View style={styles.valueRow}>
          <Text style={styles.value}>{value}</Text>
          <Icon name="calendar" size={theme.typography.sizes.base} color={theme.palette.ink2} />
        </View>
      </Pressable>
    );
  };
  const notice =
    adjusted === "today"
      ? t("mobile.progress.report.adjustedToday", {
          defaultValue: "A report ends today at the latest: the date moved to today.",
        })
      : adjusted === "other"
        ? t("mobile.progress.report.adjustedOther", {
            defaultValue: "The other date moved to keep the period in order.",
          })
        : adjusted === "capped"
          ? t("mobile.progress.report.adjustedCapped", {
              days: REPORT_MAX_DAYS,
              defaultValue:
                "A report covers up to {{days}} days: the other date moved.",
            })
          : null;
  return (
    <View style={styles.wrap}>
      <View style={styles.row}>
        {field("from")}
        {field("to")}
      </View>
      {notice ? (
        <Text style={styles.notice} accessibilityLiveRegion="polite">
          {notice}
        </Text>
      ) : null}
      <Text style={styles.hint}>
        {t("mobile.progress.report.periodHint", {
          days: REPORT_MAX_DAYS,
          defaultValue: "Any period up to {{days}} days, the past included.",
        })}
      </Text>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2 },
    row: { flexDirection: "row", gap: s.s3 },
    field: {
      flex: 1,
      minHeight: webTapFloor,
      gap: s.s1,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
    },
    pressed: { backgroundColor: p.surface2 },
    label: { color: p.ink2, fontSize: ty.sizes.base, fontWeight: "600" },
    valueRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s2,
    },
    value: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    hint: helpTextStyle(theme),
    notice: { color: p.ink, fontSize: ty.sizes.sm },
  });
}
